import logo from "./logo.svg";
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Login from "./Components/LoginPage/Login";
import SideBar from "./Components/Sidebar/SideBar";
import General from "./Components/Pages/General";
import ClientGroup from "./Components/Pages/ClientGroup";
import Packages from "./Components/Pages/Packages";
import PackageDetails from "./Components/Pages/PackageDetails";
import ClientSubscription from "./Components/Pages/ClientSubscription";
import AccountManageInformation from "./Components/Pages/AccountManageInformation";
import AppReleaseServer from "./Components/Pages/AppReleaseServer";
import Setting from "./Components/Pages/Setting";
import BusinessType from "./Components/Pages/BusinessType";
import AccountManagerMaster from "./Components/Pages/AccountManagerMaster";
import UserAuthentication from "./Components/Pages/UserAuthentication";
import Support from "./Components/Pages/Support";
import SupportTemplate from "./Components/Pages/SupportTemplate";
import ProductCategoryMaster from "./Components/Pages/ProductCategoryMaster";
import Transaction from "./Components/Pages/Transaction";
import WebTemplate from "./Components/Pages/WebTemplate";
import Dashboard from './Components/Pages/Dashboard';

import { useCookies } from "react-cookie";
import Banner from "./Components/Pages/Banner";
import PricingPage from "./Components/Pages/PricingPage";
import PaymentGateway from "./Components/Pages/PaymentGateway";
import FormDetails from "./Components/Pages/FormDetails";
import NotificationPopup from "./Components/Pages/NotificationPopup";
import UserAuthenticationApi from "./Api/UserAuthenticationApi";
import Swal from 'sweetalert2';
import { emitSseNotification } from "./utils/notificationBus";
import CouponMaster from "./Components/Pages/CouponMaster";
import PlanMaster from "./Components/Pages/PlanMaster";
import HelpDeskMaster from "./Components/Pages/HelpDesk";
import "./CSS/Responsive.css";

const Title = ({ title, children }) => {
  useEffect(() => {
    if (title == 'General') {
      document.title = 'Client List';
    } else {
      document.title = title;
    }
  }, [title]);
  return children;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [cookies] = useCookies(["token"]);
  const localToken = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  const isAuthenticated = localToken || cookies.token;

  const formAuthentication = async () => {
    let loginUser;

    try {
      const stored = localStorage.getItem('authUser');
      if (stored) {
        loginUser = JSON.parse(stored);
      }

      if (!loginUser || !loginUser.id) {
        navigate("/");
        return;
      }

      const url = window.location.pathname;
      const urlParts = url.split('/').filter(Boolean);

      const lastPart = urlParts[urlParts.length - 1];

      const formName = isNaN(lastPart)
        ? urlParts.join('/').toLowerCase()
        : urlParts.slice(0, -1).join('/').toLowerCase();

      const skipRoutes = ["", 'dashboard'];

      if (skipRoutes.includes(formName)) return;

      const formData = await UserAuthenticationApi.GetUserAuthenticationByUser(loginUser.id);

      const permissions = formData?.Result || [];

      const isAllowed = permissions.some(
        x => x.FormLink?.toLowerCase() == `/${formName}`
      );

      if (!isAllowed) {
        navigate(-1);
        await Swal.fire({
          title: "Access Denied",
          text: "You are not authorized to access this page.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
          width: "300px",
          customClass: {
            title: "swal2-custom-title",
            confirmButton: "my-confirm-button",
            popup: "swal2-custom-popup",
            htmlContainer: "swal2-custom-text",
          },
        })
      }

    } catch (error) {
      navigate("/");
    }
  };

  useEffect(() => {
    console.log("location.pathname", location.pathname)
    formAuthentication();
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const baseUrl = window?.SERVER_PORT?.url || "";
    if (!baseUrl) return;

    const cleanBase = String(baseUrl).replace(/\/$/, "");
    const sseUrl = cleanBase.endsWith("/api/v1")
      ? `${cleanBase}/notification-stream`
      : `${cleanBase}/api/v1/notification-stream`;
    let source;

    try {
      source = new EventSource(sseUrl);
    } catch (err) {
      console.error("[SSE] Failed to connect:", err);
      return;
    }

    source.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data || "{}");
        const title = msg?.title || "Notification";
        const message = msg?.message || msg?.textMessage || "";

        if (!title && !message) return;

        // Let other UI (e.g. Chatbot modal) react to the same SSE payload.
        emitSseNotification(msg);

        // Swal.fire({
        //   toast: true,
        //   position: "top-end",
        //   icon: "info",
        //   title,
        //   text: message,
        //   showConfirmButton: false,
        //   timer: 3500,
        //   timerProgressBar: true,
        //   width: "360px",
        // });
      } catch (e) {
        // Ignore invalid payloads
      }
    };

    source.onerror = (err) => {
      console.warn("[SSE] disconnected/retrying:", err);
    };

    return () => {
      try {
        source?.close();
      } catch { }
    };
  }, [isAuthenticated]);

  return (
    <div>
      <Routes>
        {/* Login-related routes */}
        <Route path="/pricing" element={<Title title="Pricing"><PricingPage /></Title>} />
        {!isAuthenticated && (
          <>
            <Route path="/" element={<Title title="Login"><Login /></Title>} />
          </>
        )}

        {/* Protected Routes */}
        {isAuthenticated && (
          <>
            <Route path="/SideBar" element={<Title title="Sidebar"><SideBar /></Title>} />
            <Route path="/General" element={<Title title="General"><General /></Title>} />
            <Route
              path="/AccountManagerMaster"
              element={<Title title="Account Master"><AccountManagerMaster /></Title>}
            />
            <Route path="/ClientGroup" element={<Title title="Client Group"><ClientGroup /></Title>} />
            <Route path="/Packages" element={<Title title="Packages"><Packages /></Title>} />
            <Route path="/PackageDetails" element={<Title title="Package Details"><PackageDetails /></Title>} />
            <Route path="/FormDetails" element={<Title title="Form Details"><FormDetails /></Title>} />
            <Route
              path="/ClientSubscription"
              element={<Title title="Client Subscription"><ClientSubscription /></Title>}
            />
            <Route
              path="/AccountManageInformation"
              element={<Title title="Account Manager Information"><AccountManageInformation /></Title>}
            />
            <Route path="/AppReleaseServer" element={<Title title="App Release Server"><AppReleaseServer /></Title>} />
            <Route path="/Setting" element={<Title title="Setting"><Setting /></Title>} />
            <Route path="/BusinessType" element={<Title title="Business Type"><BusinessType /></Title>} />
            <Route
              path="/UserAuthentication"
              element={<Title title="User Authentication"><UserAuthentication /></Title>}
            />
            <Route path="/Support" element={<Title title="Support"><Support /></Title>} />
            <Route path="/SupportTemplate" element={<Title title="Support Template"><SupportTemplate /></Title>} />
            <Route
              path="/ProductCategoryMaster"
              element={<Title title="Product Category Master"><ProductCategoryMaster /></Title>}
            />
            <Route path="/Transaction" element={<Title title="Transaction"><Transaction /></Title>} />
            <Route path="/WebTemplate" element={<Title title="Web Template"><WebTemplate /></Title>} />
            <Route path="/PaymentGateway" element={<Title title="Payment Gateway"><PaymentGateway /></Title>} />
            <Route path="/banner" element={<Title title="Banners"><Banner /></Title>} />
            {/* <Route path="/transaction/:id" component={<Transaction />} /> */}
            <Route path='/Dashboard' element={<Title title="Dashboard"><Dashboard /></Title>} />
            <Route path='/NotificationPopup' element={<Title title="Notification"><NotificationPopup /></Title>} />
            <Route path='/CouponMaster' element={<Title title="CouponMaster"><CouponMaster /></Title>} />
            <Route path='/PlanMaster' element={<Title title="PlanMaster"><PlanMaster /></Title>} />
            <Route path='/HelpDesk' element={<Title title="HelpDesk"><HelpDeskMaster /></Title>} />
          </>
        )}

        <Route
          path="*"
          element={
            isAuthenticated ? <Navigate to="/Dashboard" /> : <Navigate to="/" />
          }
        />
      </Routes>
    </div>
  );
}

export default App;
